import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AuthShell } from "./ManagerLogin";

const DEFAULT_TYPES = ["Server", "Technician", "Custodian"];

export default function JoinRestaurant() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    code: (searchParams.get("code") || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6),
    name: "",
    primary_role: "Server",
    pin: "",
  });
  const [types, setTypes] = useState(DEFAULT_TYPES);
  const [busy, setBusy] = useState(false);
  const { saveSession } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (form.code.length !== 6) return;
    api
      .get(`/auth/restaurant/${form.code}`)
      .then(({ data }) => {
        if (data.staff_types?.length) {
          setTypes(data.staff_types);
          setForm((f) => ({ ...f, primary_role: data.staff_types.includes(f.primary_role) ? f.primary_role : data.staff_types[0] }));
        }
      })
      .catch(() => {});
  }, [form.code]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/join", { ...form, code: form.code.toUpperCase() });
      saveSession(data);
      toast.success(`Welcome to ${data.restaurant.name}!`);
      navigate("/app");
    } catch (err) {
      toast.error(formatApiError(err, "Could not join business"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Join a Business" subtitle="Get the 6-character code from your manager">
      <form onSubmit={submit} className="space-y-4" data-testid="join-restaurant-form">
        <input
          required
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6) })}
          placeholder="Business code"
          data-testid="join-code-input"
          className="w-full h-12 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] font-mono tracking-[0.3em] placeholder-slate-400 placeholder-tracking-normal focus:outline-none focus:border-terra-400/60 uppercase"
        />
        <input
          required
          value={form.name}
          onChange={set("name")}
          placeholder="Your name"
          data-testid="join-name-input"
          className="w-full h-12 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] placeholder-slate-400 focus:outline-none focus:border-terra-400/60"
        />
        <select
          value={form.primary_role}
          onChange={set("primary_role")}
          data-testid="join-role-select"
          className="w-full h-12 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] focus:outline-none focus:border-terra-400/60"
        >
          {types.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <input
          required
          type="password"
          inputMode="numeric"
          pattern="\d{4}"
          maxLength={4}
          value={form.pin}
          onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
          placeholder="Choose a 4-digit PIN"
          data-testid="join-pin-input"
          className="w-full h-12 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] font-mono placeholder-slate-400 focus:outline-none focus:border-terra-400/60"
        />
        <button
          type="submit"
          disabled={busy || form.pin.length !== 4}
          data-testid="join-submit-button"
          className="w-full h-12 rounded-xl bg-terra-500 text-white font-display font-bold text-lg hover:bg-terra-600 disabled:opacity-50 transition-colors"
        >
          {busy ? "Joining…" : "Join Business"}
        </button>
      </form>
    </AuthShell>
  );
}
